
<template>
  <div class="pdf-viewer-wrapper">
    <div class="pdf-toolbar">
      <button
        class="pdf-nav-btn prev-btn"
        @click="prevPage"
        :disabled="page <= 1"
        title="Previous Page"
      >
        <i class="fas fa-chevron-left"></i>
      </button>

      <div class="page-indicator">
        <div class="page-numbers">
          <span class="current-page">{{ page }}</span>
          <span class="page-divider">/</span>
          <span class="total-pages">{{ numPages || '...' }}</span>
        </div>
      </div>

      <button
        class="pdf-nav-btn next-btn"
        @click="nextPage"
        :disabled="page >= numPages"
        title="Next Page"
      >
        <i class="fas fa-chevron-right"></i>
      </button>

      <div class="zoom-divider"></div>

      <button
        class="pdf-nav-btn zoom-btn"
        @click="zoomOut"
        :disabled="scale <= minScale"
        title="Zoom Out"
      >
        <i class="fas fa-search-minus"></i>
      </button>

      <div class="zoom-indicator">
        <span class="zoom-level">{{ zoomPercentage }}%</span>
      </div>

      <button
        class="pdf-nav-btn zoom-btn"
        @click="zoomIn"
        :disabled="scale >= maxScale"
        title="Zoom In"
      >
        <i class="fas fa-search-plus"></i>
      </button>

      <button
        class="pdf-nav-btn reset-btn"
        @click="resetZoom"
        :disabled="scale === 1"
        title="Reset Zoom"
      >
        <i class="fas fa-undo"></i>
      </button>

      <div class="zoom-divider"></div>

      <button
        class="pdf-nav-btn rotate-btn"
        @click="rotatePdf"
        title="Rotate 90°"
      >
        <i class="fas fa-sync-alt"></i>
      </button>
    </div>

    <div class="pdf-scroll-container">
      <div class="pdf-content-wrapper" :style="{ width: scale * 100 + '%' }">
        <pdf
          v-if="src"
          :src="src"
          :page="page"
          @num-pages="numPages = $event"
          @loaded="onLoaded"
          @loading="onLoading"
          class="pdf-canvas"
          :rotate="rotation"
        />
      </div>
    </div>

    <div v-if="loading" class="pdf-loading-spinner">
      <i class="fas fa-spinner fa-spin fa-2x"></i>
      <span>Loading PDF...</span>
    </div>
  </div>
</template>


<script>
import pdf from 'vue-pdf'

export default {
    name: 'PdfViewer',
    components: { pdf },
    props: {
        src: { type: String, required: true }
    },
    data() {
        return {
            page: 1,
            numPages: 0,
            scale: 1,
            minScale: 1,
            maxScale: 3,
            scaleStep: 0.25,
            rotation: 0,
            loading: true
        }
    },
    computed: {
        zoomPercentage() {
            return Math.round(this.scale * 100)
        }
    },
    methods: {
        onLoaded() {
            this.loading = false
        },
        onLoading() {
            this.loading = true
        },
        nextPage() {
            if (this.page < this.numPages) this.page++
        },
        prevPage() {
            if (this.page > 1) this.page--
        },
        zoomIn() {
            if (this.scale < this.maxScale) {
                this.scale = Math.min(this.scale + this.scaleStep, this.maxScale)
            }
        },
        zoomOut() {
            if (this.scale > this.minScale) {
                this.scale = Math.max(this.scale - this.scaleStep, this.minScale)
            }
        },
        resetZoom() {
            this.scale = 1
        },
        rotatePdf() {
            this.rotation = (this.rotation + 90) % 360
        }
    }
}
</script>


<style scoped>
.pdf-viewer-wrapper {
  width: 100%;
  height: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
}

.pdf-scroll-container {
  width: 100%;
  flex: 1;
  overflow: auto;
  position: relative;
}

.pdf-content-wrapper {
  display: inline-block;
  margin: 0 auto;
  transition: transform 0.3s ease;
}

.pdf-canvas {
  width: 100%;
  background: #fff;
  border: 1px solid #eee;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  border-radius: 4px;
}

/* Modern Pagination Toolbar */
.pdf-toolbar {
  display: flex;
  align-items: center;
  padding: 0.5rem 0;
}

/* Navigation Buttons */
.pdf-nav-btn {
  background: rgba(255, 255, 255, 0.95);
  border: none;
  border-radius: 6px;
  padding: 8px 14px;
  cursor: pointer;
  color: #667eea;
  font-size: 1rem;
  font-weight: 600;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 40px;
}

.pdf-nav-btn:hover:not(:disabled) {
  background: #ffffff;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  color: #764ba2;
}

.pdf-nav-btn:active:not(:disabled) {
  transform: translateY(0);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.pdf-nav-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
  background: rgba(255, 255, 255, 0.6);
  color: #999;
  box-shadow: none;
}

.pdf-nav-btn i {
  transition: transform 0.3s ease;
}

.prev-btn:hover:not(:disabled) i {
  transform: translateX(-2px);
}

.next-btn:hover:not(:disabled) i {
  transform: translateX(2px);
}

/* Page Indicator */
.page-indicator {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 6px 20px;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 6px;
  backdrop-filter: blur(10px);
  min-width: 120px;
}

.page-label {
  font-size: 0.7rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.8);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.page-numbers {
  display: flex;
  align-items: baseline;
  gap: 6px;
}

.current-page {
  font-size: 0.9rem;
  font-weight: 500;
  color: #495057;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.page-divider {
  font-size: 0.9rem;
  color: #495057;
  font-weight: 400;
}

.total-pages {
  font-size: 0.9rem;
  font-weight: 500;
  color: #495057;
}

/* Zoom Controls */
.zoom-divider {
  width: 1px;
  height: 24px;
  background: rgba(0, 0, 0, 0.15);
  margin: 0 12px;
}

.zoom-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px 12px;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 6px;
  backdrop-filter: blur(10px);
  min-width: 60px;
}

.zoom-level {
  font-size: 0.85rem;
  font-weight: 500;
  color: #495057;
}

.zoom-btn,
.reset-btn {
  margin: 0 2px;
}

/* Loading Spinner */
.pdf-loading-spinner {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  color: #667eea;
  background: rgba(255, 255, 255, 0.95);
  z-index: 10;
  backdrop-filter: blur(4px);
}

.pdf-loading-spinner span {
  margin-top: 12px;
  font-weight: 600;
  font-size: 0.9rem;
  color: #667eea;
}

/* Responsive Design */
@media (max-width: 768px) {
  .pdf-toolbar {
    padding: 8px 12px;
    gap: 8px;
  }

  .pdf-nav-btn {
    padding: 6px 10px;
    min-width: 36px;
  }

  .page-indicator {
    padding: 4px 12px;
    min-width: 100px;
  }

  .current-page {
    font-size: 1.1rem;
  }

  .page-label {
    font-size: 0.65rem;
  }

  .zoom-divider {
    margin: 0 8px;
  }

  .zoom-indicator {
    padding: 4px 8px;
    min-width: 50px;
  }

  .zoom-level {
    font-size: 0.75rem;
  }
}
</style>